/**
 * Clock In Dialog Component
 * Displays session information, location details, and map with provider's current location
 */

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material'
import {
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from '@react-google-maps/api'
import { format } from 'date-fns'
import { Session, Patient, Therapist, SessionStatus } from '@/types/contracts'
import api from '@/services/api'
import EarthLoader from '@/components/loaders/EarthLoader'
import { GOOGLE_MAPS_LIBRARIES, getGoogleMapsApiKey } from '@/constants/googleMaps'

interface ClockInDialogProps {
  open: boolean
  onClose: () => void
  session: Session
  patient: Patient
  therapist: Therapist
  contractId: string
  onSuccess: (updatedSession: Session) => void
}

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px',
}

const defaultCenter = {
  lat: 34.0522,
  lng: -118.2437,
}

const ClockInDialog: React.FC<ClockInDialogProps> = ({
  open,
  onClose,
  session,
  patient,
  therapist,
  contractId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [location, setLocation] = useState<{
    lat: number
    lng: number
    address: string
  } | null>(null)
  const [locationError, setLocationError] = useState<string>('')
  const [verifying, setVerifying] = useState(false)
  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [infoWindowOpen, setInfoWindowOpen] = useState(true)

  const apiKey = getGoogleMapsApiKey()
  
  // Use constant libraries array to prevent recreation on each render
  // This ensures consistency and prevents conflicts when navigating between pages
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  // Fetch location when dialog opens
  useEffect(() => {
    if (open) {
      fetchLocation()
    } else {
      // Reset state when dialog closes
      setLocation(null)
      setLocationError('')
      setVerifying(false)
    }
  }, [open])

  const fetchLocation = async () => {
    setVerifying(true)
    setLocationError('')
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser')
      }

      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            maximumAge: 0,
          })
        }
      )

      const lat = position.coords.latitude
      const lng = position.coords.longitude

      // Set map center immediately
      setMapCenter({ lat, lng })

      // Try to get address from reverse geocoding
      let address = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`

      // Set initial location with coordinates
      setLocation({ lat, lng, address })

      // Try to get formatted address if Google Maps is loaded
      if (window.google?.maps && isLoaded) {
        try {
          const geocoder = new window.google.maps.Geocoder()
          const latlng = { lat, lng }
          geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              setLocation({
                lat,
                lng,
                address: results[0].formatted_address,
              })
            }
          })
        } catch (error) {
          // If geocoding fails, keep the coordinate-based address
          console.warn('Geocoding failed:', error)
        }
      }
    } catch (error: any) {
      setLocationError(
        error.message ||
          'Unable to retrieve location. Please check your location permissions.'
      )
    } finally {
      setVerifying(false)
    }
  }

  const handleClockIn = async () => {
    if (!location) {
      setLocationError('Location is required to clock in')
      return
    }

    setLoading(true)
    try {
      const response = await api.post('/sessions/check-in', {
        contract_id: contractId,
        session_id: session._id || session.id,
        lat: location.lat.toString(),
        long: location.lng.toString(),
      })

      // Create updated session with check-in time
      const updatedSession: Session = {
        ...session,
        evv: {
          ...session.evv,
          checkInAt: new Date().toISOString(),
          lat: location.lat,
          lng: location.lng,
          checkInLocation: {
            lat: location.lat,
            lng: location.lng,
            address: location.address,
          },
        },
        status: 'in-progress' as SessionStatus,
      }

      onSuccess(updatedSession)
      onClose()
    } catch (error: any) {
      setLocationError(
        error.response?.data?.message || 'Failed to clock in. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const getSessionType = () => {
    // Try to get from service or default to Physical Therapy
    return 'Physical Therapy' // This should come from contract.service or session data
  }

  const getModality = () => {
    return session.locationType || 'in-person'
  }

  const getSlotDuration = () => {
    return session.slotDurationMinutes || session.durationMinutes || 60
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOnIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Clock In
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Session ID: {session.id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Session Information */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.secondary"
            mb={2}
          >
            Session Information
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Patient Name
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {patient.name}
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Provider Name
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {therapist.name}
              </Typography>
            </Box>

            {/* <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <PeopleIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Session Type
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {getSessionType()}
              </Typography>
            </Box> */}

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Date & Time of Session
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {format(new Date(session.scheduledAt), 'MMM dd, yyyy h:mm a')}
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Slot Duration
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {getSlotDuration()} minutes
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOnIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Modality
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {getModality()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Location Details */}
        {verifying && (
          <Box
            mb={3}
            sx={{
              minHeight: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'primary.main',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              p: 2,
            }}
          >
            <EarthLoader message="Fetching your location..." />
          </Box>
        )}

        {location && !verifying && (
          <Box mb={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" alignItems="start" gap={1.5}>
                <LocationOnIcon fontSize="small" color="primary" />
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600} mb={0.5}>
                    {location.address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Lat: {location.lat.toFixed(6)}, Lng:{' '}
                    {location.lng.toFixed(6)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        {locationError && !verifying && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {locationError}
          </Alert>
        )}

        {/* Map View */}
        {location && isLoaded && (
          <Box mb={2}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={15}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
            >
              <Marker
                position={{ lat: location.lat, lng: location.lng }}
                onClick={() => setInfoWindowOpen(true)}
                icon={{
                  url:
                    'data:image/svg+xml;base64,' +
                    btoa(`
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="24" cy="24" r="20" fill="#2196F3" opacity="0.3"/>
                      <circle cx="24" cy="24" r="16" fill="#2196F3" opacity="0.5"/>
                      <circle cx="24" cy="24" r="12" fill="#2196F3"/>
                      <circle cx="24" cy="24" r="6" fill="white"/>
                    </svg>
                  `),
                  scaledSize: new window.google.maps.Size(48, 48),
                  anchor: new window.google.maps.Point(24, 24),
                }}
              >
                {infoWindowOpen && (
                  <InfoWindow
                    onCloseClick={() => setInfoWindowOpen(false)}
                    position={{ lat: location.lat, lng: location.lng }}
                  >
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} mb={0.5}>
                        Provider Location
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {location.address}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Lat: {location.lat.toFixed(6)}, Lng:{' '}
                        {location.lng.toFixed(6)}
                      </Typography>
                    </Box>
                  </InfoWindow>
                )}
              </Marker>
            </GoogleMap>
          </Box>
        )}

        {!location && !verifying && !locationError && (
          <Box
            sx={{
              height: 300,
              backgroundColor: 'grey.100',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.secondary">
              Waiting for location...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleClockIn}
          disabled={!location || loading || verifying}
          startIcon={
            loading ? <CircularProgress size={16} /> : <CheckCircleIcon />
          }
          sx={{
            minWidth: 120,
          }}
        >
          Clock In
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ClockInDialog
